import classNames from "classnames";
import {
	combineAttachmentKinds,
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames,
	NO_CONTROL_ATTACHMENTS
} from "../../ControlAttachment";
import { Icon } from "../../Icon/Icon";
import { Select } from "../../Select/Select";
import {
	ReactElement,
	useCallback,
	useContext,
	useMemo
} from "react";
import { ApplyColorControl } from "./ApplyColorControl/ApplyColorControl";
import { BasicToolHandlers, useBasicTools } from "../tools/useBasicTools";
import { ColorPickerToolHandler, useColorPickerTool } from "../tools/useColorPickerTool";
import { FontFamilyToolHandlers, useFontFamilyTool } from "../tools/useFontFamilyTool";
import { FontSizeToolHandlers, useFontSizeTool } from "../tools/useFontSizeTool";
import { useIndentionTools } from "../tools/useIndentionTools";
import { LinkToolHandlers, useLinkTool } from "../tools/useLinkTool";
import { ListToolHandlers, useListTools } from "../tools/useListTools";
import { useTextAlignTools } from "../tools/useTextAlignTools";
import { RichTextEditorState } from "../types";
import { Button } from "./Button/Button";
import { InsertTableControl } from "./InsertTableControl/InsertTableControl";
import { TableToolHandlers, useTableTools } from "../tools/useTableTools";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil,
	setTrackingId,
	getDataUrl
} from "client/Misc/utils";
import styles from "./RichTextEditorToolbar.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { ImageToolHandlers, useImageTools } from "../tools/useImageTools";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	AlertManager,
	useAlerts,
	useValueEqualMemo
} from "client/Hooks";
import { InsertImage } from "./InsertImage";
import {
	RichTextEditorToolProps,
	useProseMirrorRichTextEditorTool
} from "../tools/useProseMirrorRichTextEditorTool";
import { ButtonGroup } from "./ButtonGroup/ButtonGroup";
import { useEraserTool } from "../tools/useEraserTool";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { EmojiToolHandlers, useEmojiTool } from "../tools/useEmojiTool";
import { InsertEmojiControl } from "./InsertEmojiControl";

export type RichTextEditorImageConstraints = {
	supportedImageTypes: readonly string[];
	maxFileSize: number;
};

export type RichTextEditorImageToolOptions = {
	imageConstraints?: RichTextEditorImageConstraints;
	errorMessage?: string;
};

type RichTextEditorToolbarFeatureWithoutOptions =
	| "basic-tools"
	| "link-tool"
	| "font-style-tools"
	| "font-color-tool"
	| "text-align-tools"
	| "indention"
	| "list-tools"
	| "table-tools"
	| "eraser-tool"
	| "emoji-tool";

type RichTextEditorToolbarFeatureWithOptionsName =
	| "insert-image-tool";

type RichTextEditorToolbarFeatureName =
	| RichTextEditorToolbarFeatureWithoutOptions
	| RichTextEditorToolbarFeatureWithOptionsName;

type RichTextEditorToolbarFeatureOptions<
	T extends RichTextEditorToolbarFeatureName
> = T extends "insert-image-tool"
		? RichTextEditorImageToolOptions
		: boolean;

export type RichTextEditorToolbarFeatureWithOptions<T extends RichTextEditorToolbarFeatureWithOptionsName> =
	{ kind: T; }
	& RichTextEditorToolbarFeatureOptions<T>;

export type RichTextEditorToolbarFeature =
	| RichTextEditorToolbarFeatureWithoutOptions
	| RichTextEditorToolbarFeatureWithOptions<"insert-image-tool">;

export const defaultRichTextEditorFeatures: readonly RichTextEditorToolbarFeature[] = [
	"basic-tools",
	"link-tool",
	"font-style-tools",
	"font-color-tool",
	"text-align-tools",
	"indention",
	"list-tools",
	"table-tools",
	"eraser-tool",
	"emoji-tool"
];

export function imageTool(
	options: RichTextEditorImageToolOptions
): RichTextEditorToolbarFeatureWithOptions<"insert-image-tool"> {
	return {
		...options,
		kind: "insert-image-tool"
	};
}

export type RichTextEditorToolbarProps = {
	value: RichTextEditorState;
	onChange(newState: RichTextEditorState, isSignificant: boolean): void;
	onFocusInput: (() => void) | "NONE";
	isInvalid?: boolean;
	supportedFeatures?: readonly RichTextEditorToolbarFeature[];
	renderCustomTool?(props: RichTextEditorToolProps): ReactElement | null;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	attachedOn?: ControlAttachmentOptions;
	trackingId?: string;
	isDisabled?: boolean;
	areHashUrlsAllowed?: boolean;
};

export const RichTextEditorToolbar = (props: RichTextEditorToolbarProps): ReactElement => {
	const {
		value,
		onChange,
		onFocusInput,
		isInvalid,
		supportedFeatures: supportedFeaturesArray = defaultRichTextEditorFeatures,
		renderCustomTool,
		attachedOn = EMPTY_ARRAY,
		hasNoPadding = false,
		isBorderless = false,
		isDisabled = false,
		areHashUrlsAllowed = false,
		trackingId
	} = props;

	const {
		editorState,
		applyCommand,
		applyCommandSequence,
		focus: focusInputHandler
	} = useProseMirrorRichTextEditorTool({
		value,
		onChange,
		onFocusInput
	});

	const alertManager: AlertManager = useAlerts();

	const supportedFeatures = useValueEqualMemo(
		(): Map<RichTextEditorToolbarFeatureName, RichTextEditorToolbarFeatureOptions<RichTextEditorToolbarFeatureName>> => {
			return fromArray(
				supportedFeaturesArray,
				f => typeof f === "string" ? f : f.kind,
				f => typeof f === "string" ? true : f
			);
		},
		[supportedFeaturesArray]
	);

	const basicTools: BasicToolHandlers = useBasicTools(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const linkTool: LinkToolHandlers = useLinkTool(
		editorState,
		applyCommand,
		areHashUrlsAllowed,
		focusInputHandler
	);

	const fontSizeTool: FontSizeToolHandlers = useFontSizeTool(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const fontFamilyTool: FontFamilyToolHandlers = useFontFamilyTool(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const colorTool: ColorPickerToolHandler = useColorPickerTool(
		editorState,
		applyCommand
	);

	const {
		alignLeftHandler,
		alignRightHandler,
		alignCenterHandler,
		alignJustifyContentHandler,
		isTextAlignActive
	} = useTextAlignTools(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const {
		increaseIndentionHandler,
		decreaseIndentionHandler,
		hasIndention
	} = useIndentionTools(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const listTools: ListToolHandlers = useListTools(
		editorState,
		applyCommandSequence,
		focusInputHandler
	);

	const tableTools: TableToolHandlers = useTableTools(
		editorState,
		applyCommandSequence,
		focusInputHandler
	);

	const imageTool: ImageToolHandlers = useImageTools(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const {
		canErase,
		eraseHandler
	} = useEraserTool(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const emojiTool: EmojiToolHandlers = useEmojiTool(
		editorState,
		applyCommand,
		focusInputHandler
	);

	const imageOptions = useMemo(
		(): RichTextEditorImageToolOptions | undefined => {
			const options: boolean | RichTextEditorImageToolOptions | undefined = supportedFeatures
				.get("insert-image-tool");

			if (isNil(options)) {
				return undefined;
			}

			if (typeof options === "boolean") {
				throw new Error("The insert image tool options must be an object");
			}

			return options;
		},
		[supportedFeatures]
	);

	const insertImageHandler = useCallback(
		async (file: File): Promise<void> => {
			if (!imageTool.isSupportedBySchema) {
				return;
			}

			if (!imageOptions) {
				return;
			}

			const {
				errorMessage,
				imageConstraints
			} = imageOptions;

			if (imageConstraints) {
				const {
					size,
					type
				} = file;

				const {
					supportedImageTypes,
					maxFileSize
				} = imageConstraints;

				const isTypeSupported: boolean = supportedImageTypes.indexOf(type) !== -1;
				const isWithinSizeLimit = size <= maxFileSize;

				if (!isTypeSupported || !isWithinSizeLimit) {
					alertManager.showError(
						errorMessage ?? localize("_InvalidImageUpload")
					);
					return;
				}
			}

			const dataUrl: string = await getDataUrl(file);

			imageTool.insertImage(dataUrl);
		},
		[
			alertManager,
			imageOptions,
			imageTool
		]
	);

	const {
		attachedOn: attachmentsFromContext
	} = useContext(ControlAttachmentContext);

	const isAttachedOnBottom: boolean = combineAttachmentKinds(
		attachedOn,
		attachmentsFromContext
	).some(k => k === "bottom");

	return (
		<NoUniqueInputIdContext>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_invalid]: isInvalid,
						[styles.root_attachedOnBottom]: isAttachedOnBottom,
						[styles.root_borderless]: isBorderless,
						[styles.root_noPadding]: hasNoPadding,
						[styles.root_isDisabled]: isDisabled
					},
					getAttachmentClassNames(attachedOn, attachmentsFromContext)
				)}
			>
				<ControlAttachmentContext.Provider
					value={NO_CONTROL_ATTACHMENTS}
				>
					{supportedFeatures.get("basic-tools") && basicTools.areSupportedBySchema && (
						<ButtonGroup>
							<Button
								onClick={basicTools.boldHandler}
								isActive={basicTools.isBold}
								attachedOn="right"
								trackingId="bold-toggle"
								isDisabled={isDisabled}
								tooltip={localize("_Bold")}
							>
								<Icon value="bold" />
							</Button>
							<Button
								onClick={basicTools.italicHandler}
								isActive={basicTools.isItalic}
								attachedOn={["left", "right"]}
								trackingId="italic-toggle"
								isDisabled={isDisabled}
								tooltip={localize("_Italic")}
							>
								<Icon value="italic" />
							</Button>
							<Button
								onClick={basicTools.underlineHandler}
								isActive={basicTools.isUnderline}
								attachedOn={["left", "right"]}
								trackingId="underline-toggle"
								isDisabled={isDisabled}
								tooltip={localize("_Underline")}
							>
								<Icon value="underline" />
							</Button>
							<Button
								onClick={basicTools.strikeThroughHandler}
								isActive={basicTools.isStrikeThrough}
								attachedOn={["left"]}
								trackingId="line-through-toggle"
								isDisabled={isDisabled}
								tooltip={localize("_Strikethrough")}
							>
								<Icon value="strikethrough" />
							</Button>
						</ButtonGroup>
					)}
					{supportedFeatures.get("font-style-tools") && (
						<>
							{fontFamilyTool.isSupportedBySchema && (
								<ButtonGroup doesContainSingleInput>
									<Select
										value={fontFamilyTool.fontFamily}
										onChange={fontFamilyTool.fontFamilyChangeHandler}
										options={fontFamilyTool.fontFamilyOptions}
										menuTitle={localize("_FontFamily")}
										trackingId="font-family-selector"
										isDisabled={isDisabled}
										tooltip={localize("_FontFamily")}
									/>
								</ButtonGroup>
							)}
							{fontSizeTool.isSupportedBySchema && (
								<ButtonGroup doesContainSingleInput>
									<Select
										value={fontSizeTool.fontSize}
										onChange={fontSizeTool.fontSizeChangeHandler}
										options={fontSizeTool.fontSizeOptions}
										menuTitle={localize("_FontSize")}
										trackingId="font-size-selector"
										isDisabled={isDisabled}
										tooltip={localize("_FontSize")}
									/>
								</ButtonGroup>
							)}
						</>
					)}
					{supportedFeatures.get("font-color-tool") && colorTool.isSupportedBySchema && (
						<ButtonGroup>
							<ApplyColorControl
								value={colorTool.color}
								onChange={colorTool.colorChangeHandler}
								trackingId="font-color-picker"
								menuTitle={localize("_FontColor")}
								isDisabled={isDisabled}
								tooltip={localize("_FontColor")}
							/>
						</ButtonGroup>
					)}
					{supportedFeatures.get("eraser-tool") && (
						<ButtonGroup>
							<Button
								onClick={eraseHandler}
								trackingId="erase"
								isDisabled={isDisabled || !canErase}
								tooltip={localize("_RemoveStyling")}
							>
								<Icon value="eraser" />
							</Button>
						</ButtonGroup>
					)}
					{supportedFeatures.get("emoji-tool") && emojiTool.isSupportedBySchema && (
						<ButtonGroup>
							<InsertEmojiControl
								onInsertEmoji={emojiTool.insertEmoji}
								trackingId="insert-emoji"
								isDisabled={isDisabled}
								tooltip={localize("_Emoji")}
							/>
						</ButtonGroup>
					)}
					{supportedFeatures.get("text-align-tools") && (
						<ButtonGroup>
							<Button
								onClick={alignLeftHandler}
								attachedOn="right"
								isActive={isTextAlignActive("left")}
								trackingId="paragraph-left"
								isDisabled={isDisabled}
								tooltip={localize("_AlignLeft")}
							>
								<Icon value="align-left" />
							</Button>
							<Button
								onClick={alignCenterHandler}
								attachedOn={["left", "right"]}
								isActive={isTextAlignActive("center")}
								trackingId="paragraph-center"
								isDisabled={isDisabled}
								tooltip={localize("_AlignCenter")}
							>
								<Icon value="align-center" />
							</Button>
							<Button
								onClick={alignRightHandler}
								attachedOn={["left", "right"]}
								isActive={isTextAlignActive("right")}
								trackingId="paragraph-right"
								isDisabled={isDisabled}
								tooltip={localize("_AlignRight")}
							>
								<Icon value="align-right" />
							</Button>
							<Button
								onClick={alignJustifyContentHandler}
								attachedOn="left"
								isActive={isTextAlignActive("justify")}
								trackingId="paragraph-justify"
								isDisabled={isDisabled}
								tooltip={localize("_Justify ")}
							>
								<Icon value="align-justify" />
							</Button>
						</ButtonGroup>
					)}
					{(supportedFeatures.get("indention") || supportedFeatures.get("list-tools")) && (
						<ButtonGroup>
							{supportedFeatures.get("list-tools") && listTools.areSupportedBySchema && (
								<>
									<Button
										onClick={listTools.toggleBulletListHandler}
										attachedOn="right"
										isActive={listTools.isInBulletList}
										trackingId="bullet-list"
										isDisabled={isDisabled}
										tooltip={localize("_Bullets")}
									>
										<Icon value="list" />
									</Button>
									<Button
										onClick={listTools.toggleOrderedListHandler}
										attachedOn={["left", supportedFeatures.get("indention") ? "right" : undefined]}
										isActive={listTools.isInOrderedList}
										trackingId="ordered-list"
										isDisabled={isDisabled}
										tooltip={localize("_Numbering")}
									>
										<Icon value="list-ol" />
									</Button>
								</>
							)}
							{supportedFeatures.get("indention") && (
								<>
									<Button
										onClick={increaseIndentionHandler}
										attachedOn={[supportedFeatures.get("list-tools") ? "left" : undefined, "right"]}
										trackingId="increase-indention"
										isDisabled={isDisabled}
										tooltip={localize("_IncreaseIndent")}
									>
										<Icon value="indent" />
									</Button>
									<Button
										onClick={decreaseIndentionHandler}
										isDisabled={isDisabled || !hasIndention}
										attachedOn="left"
										trackingId="decrease-indention"
										tooltip={localize("_DecreaseIndent")}
									>
										<Icon value="outdent" />
									</Button>
								</>
							)}
						</ButtonGroup>
					)}
					{supportedFeatures.get("link-tool") && linkTool.isSupportedBySchema && (
						<ButtonGroup>
							<Button
								onClick={linkTool.addOrUpdateLinkHandler}
								attachedOn="right"
								trackingId="add-link"
								isDisabled={isDisabled}
								tooltip={localize("_InsertHyperlink")}
							>
								<Icon value="link" />
							</Button>
							<Button
								onClick={linkTool.removeLinkHandler}
								attachedOn="left"
								isDisabled={isDisabled || !linkTool.isLink}
								trackingId="remove-link"
								tooltip={localize("_RemoveHyperlink")}
							>
								<Icon value="link-slash" />
							</Button>
						</ButtonGroup>
					)}
					{supportedFeatures.get("table-tools") && tableTools.areSupportedBySchema && (
						<>
							<ButtonGroup>
								<InsertTableControl
									onInsertTable={tableTools.insertTable}
									trackingId="insert-table-control"
									isDisabled={isDisabled}
									tooltip={localize("_InsertTable")}
								/>
							</ButtonGroup>
							{tableTools.isInTable && (
								<ButtonGroup>
									<Button
										onClick={tableTools.addColumnBefore}
										attachedOn="right"
										trackingId="table-add-column-before"
										isDisabled={isDisabled}
										tooltip={localize("_InsertLeft")}
									>
										<Icon value="column-add-left" />
									</Button>
									<Button
										onClick={tableTools.addColumnAfter}
										attachedOn={["left", "right"]}
										trackingId="table-add-column-after"
										isDisabled={isDisabled}
										tooltip={localize("_InsertRight")}
									>
										<Icon value="column-add-right" />
									</Button>
									<Button
										onClick={tableTools.addRowAbove}
										attachedOn={["left", "right"]}
										trackingId="table-add-row-above"
										isDisabled={isDisabled}
										tooltip={localize("_InsertAbove")}
									>
										<Icon value="row-add-top" />
									</Button>
									<Button
										onClick={tableTools.addRowBelow}
										attachedOn={["left", "right"]}
										trackingId="table-add-row-below"
										isDisabled={isDisabled}
										tooltip={localize("_InsertBelow")}
									>
										<Icon value="row-add-bottom" />
									</Button>
									<Button
										onClick={tableTools.removeRow}
										attachedOn={["left", "right"]}
										trackingId="table-remove-row"
										isDisabled={isDisabled}
										tooltip={localize("_DeleteRow")}
									>
										<Icon value="row-delete" />
									</Button>
									<Button
										onClick={tableTools.removeColumn}
										attachedOn="left"
										trackingId="table-remove-column"
										isDisabled={isDisabled}
										tooltip={localize("_DeleteColumn")}
									>
										<Icon value="column-delete" />
									</Button>
								</ButtonGroup>
							)}
						</>
					)}
					{linkTool.isSupportedBySchema && linkTool.editLinkDialog}
					{imageOptions && imageTool.isSupportedBySchema && (
						<ButtonGroup>
							<InsertImage
								insertImageHandler={insertImageHandler}
								options={imageOptions}
								isDisabled={isDisabled}
							/>
						</ButtonGroup>
					)}
					{isNotNil(renderCustomTool) &&
						renderCustomTool({
							value,
							onChange,
							onFocusInput
						})
					}
				</ControlAttachmentContext.Provider>
			</div>
		</NoUniqueInputIdContext>
	);
};
RichTextEditorToolbar.displayName="RichTextEditorToolbar";
RichTextEditorToolbar.ButtonGroup = ButtonGroup;
RichTextEditorToolbar.Button = Button;